import { useRef, useState } from "react";
import { Car, TriangleAlert } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { useIntersectionLoadMore } from "@/hooks/useIntersectionLoadMore";
import { ShipmentListLoadingSkeleton } from "@/features/shipments/ShipmentListItemSkeleton";
import { ShipmentListFilterBar, useShipmentListFilters } from "@/features/shipments/ShipmentListFilters";
import ShipmentsEmptyState from "@/components/shipments/ShipmentsEmptyState";

export default function ShipmentList({
  shipments,
  selectedId,
  onSelect,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: {
  shipments: any[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef(0);

  const loadMoreSentinelRef = useIntersectionLoadMore({
    enabled: hasMore,
    isLoading: isLoadingMore,
    onLoadMore,
    rootRef: listRef,
    observeKey: shipments.length,
    resetKey: selectedId,
  });

  const handleSelect = (id: string) => {
    listScrollRef.current = listRef.current?.scrollTop ?? 0;
    onSelect(id);
  };

  const {
    search,
    setSearch,
    status,
    setStatus,
    dateSort,
    setDateSort,
    statusOptions,
    filtered,
  } = useShipmentListFilters(shipments);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-4 py-3">
        <ShipmentListFilterBar
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
          dateSort={dateSort}
          onDateSortChange={setDateSort}
          statusOptions={statusOptions}
          searchInputClassName="bg-white"
        />
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
        {filtered.length === 0 ? (
          <ShipmentsEmptyState variant="no-results" query={search} />
        ) : null}
        {filtered.map((s) => {
          const isSelected = selectedId === s.id;
          return (
            <div
              key={s.id}
              onClick={() => handleSelect(s.id)}
              className={`bg-white border rounded-[12px] p-4 cursor-pointer transition-all ${
                isSelected ? "border-[#2746EB] bg-[#F0F4FF]" : "border-[#E4E7EC] hover:bg-[#F9FAFB]"
              }`}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Car size={14} color="#475367" className="flex-shrink-0" />
                  <span className="text-[10px] text-[#475367] font-[600] truncate">
                    #{s.number}
                  </span>
                </div>
                <StatusBadge status={s.status} className="max-w-[44vw] sm:max-w-none" />
              </div>

              <h4 className="text-[14px] font-[600] text-[#1D2739] mb-3 truncate">{s.vehicle}</h4>

              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] text-[#344054] mb-1 truncate" title={s.from}>
                    {s.from}
                  </p>
                  <p className="text-[14px] font-[500] text-[#101928]">{s.date}</p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1 text-[11px] text-[#667185] self-center">
                  <span className="text-[#475367]">●- - -</span>
                  <span className="border border-[#D0D5DD] bg-[#F0F2F5] rounded-full px-2 py-0.5 text-[11px] text-[#344054] whitespace-nowrap">
                    ⏱ {s.eta}
                  </span>
                  <span className="text-[#475367]">- - -●</span>
                </div>
                <div className="text-right min-w-0 flex-1">
                  <p className="text-[12px] text-[#344054] mb-1 truncate" title={s.to}>
                    {s.to}
                  </p>
                  <p className="text-[14px] font-[500] text-[#101928]">{s.arrival}</p>
                </div>
              </div>

              {s._raw?.balance > 0 && (
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#E4E7EC]">
                  <div className="flex items-center gap-2.5">
                    <TriangleAlert size={16} color="#D92D20" />
                    <span className="text-[14px] font-[600] text-[#BA110B] ">
                      Payment due · ${s._raw?.balance?.toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="text-[12px] font-[600] text-[#344054] border border-[#D0D5DD] bg-white px-3 py-0.5 rounded-[8px] hover:bg-[#F9FAFB] transition-colors"
                  >
                    Pay now
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length > 0 && (hasMore || isLoadingMore) && hasMore ? (
          <div ref={loadMoreSentinelRef} className="h-px" aria-hidden />
        ) : null}
        {filtered.length > 0 && isLoadingMore ? <ShipmentListLoadingSkeleton rows={2} /> : null}
        {!hasMore && filtered.length >= 6 && !isLoadingMore ? (
          <p className="text-center text-[11px] text-[#98A2B3] py-2">You&apos;ve reached the end</p>
        ) : null}
      </div>
    </div>
  );
}
